export const DAY = 24 * 60 * 60 * 1000;
const number = (value) => value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value);
export const historyCutoff = (history, now, historyDays) => historyDays === "all"
  ? history[0]?.time ?? now - DAY
  : now - historyDays * DAY;
const parseEvents = (events) => {
  if (typeof events === "string") {
    try { events = JSON.parse(events); } catch (_) { return []; }
  }
  return Array.isArray(events) ? events.filter((event) => event && typeof event === "object") : [];
};
export const eventTotals = (events) => {
  return parseEvents(events).reduce((result, event) => {
    ["dispatch", "manufacturing", "resupply", "correction", "manual_edit", "lifecycle_state", "device_stock_sync"].forEach((key) => {
      if (number(event[key]) != null) result[key] = (result[key] || 0) + Number(event[key]);
    });
    result.consumption = (result.consumption || 0) + Math.max(0, -(number(event.dispatch) || 0)) + Math.max(0, -(number(event.manufacturing) || 0));
    return result;
  }, {});
};

// Projection rows contain delivery quantities, not inventory snapshots.
export const stockHistory = (rows) => (rows || [])
  .filter((row) => !row.projection && Number.isFinite(Date.parse(row.createdAt)) && (number(row.free) != null || number(row.total) != null))
  .map((row) => ({ ...row, time: Date.parse(row.createdAt), hasEvents: parseEvents(row.events).length > 0, events: eventTotals(row.events) }))
  .sort((a, b) => a.time - b.time || (a.id || 0) - (b.id || 0));

const itemQuantity = (items, itemId) => (Array.isArray(items) ? items : [])
  .filter((item) => String(item.StockItemId || item.id) === String(itemId))
  .reduce((sum, item) => sum + Math.max(0, number(item.count) || 0), 0);

export const incomingDeliveries = (rows, now, warehouseName) => {
  const deliveries = [];
  const seen = new Set();
  let overdue = 0;
  (rows || []).forEach((row) => {
    const order = row.ResupplyOrder;
    if (!row.projection || !order || seen.has(order.id) ||
      !["invoiced", "deposit", "completed", "transit"].includes(order.paymentStatus)) return;
    seen.add(order.id);
    const itemId = row.StockItemId || row.StockItem?.id;
    const shipments = order.Shipments || [];
    let remaining = itemQuantity(order.genericDevices, itemId);
    const add = (date, quantity) => {
      // A date-only ETA remains due today until the end of the local calendar day.
      const time = typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? new Date(`${date}T23:59:59`).getTime() : Date.parse(date);
      if (!(quantity > 0) || !Number.isFinite(time)) return;
      if (time < now) { overdue += quantity; return; }
      deliveries.push({ time, quantity, number: order.number });
    };
    shipments.forEach((shipment) => {
      if (!["delivered", "waiting", "transit"].includes(shipment.status)) return;
      const quantity = Math.min(remaining, itemQuantity(shipment.genericDevices, itemId));
      remaining -= quantity;
      if (shipment.status !== "delivered" && shipment.shippingInfo?.companyName === warehouseName) {
        add(shipment.eta || order.eta, quantity);
      }
    });
    if (order.destination === warehouseName) add(order.eta, remaining);
  });
  return { deliveries: deliveries.sort((a, b) => a.time - b.time), overdue };
};

export const calculateForecast = (rows, { now = Date.now(), historyDays = 90, free, total, alertQuantity = 0, warehouseName, reserved = 0, subtractReservations = false } = {}) => {
  const history = stockHistory(rows).filter((row) => row.time <= now);
  const latest = history[history.length - 1];
  const currentFree = number(free) == null ? number(latest?.free) : number(free);
  const available = currentFree == null ? null : currentFree - (subtractReservations ? Math.max(0, number(reserved) || 0) : 0);
  const physical = number(total) == null ? number(latest?.total) : number(total);
  const cutoff = historyCutoff(history, now, historyDays);
  const start = history.length ? Math.max(cutoff, history[0].time) : now;
  const days = (now - start) / DAY;
  let consumed = 0;
  let observations = history.some((row) => row.time <= start) ? 1 : 0;
  let legacy = false;
  history.forEach((row, index) => {
    if (!index || row.time <= start) return;
    observations++;
    if (row.hasEvents) {
      // Reservations, corrections, transfers and manufacturing output are not demand.
      consumed += row.events.consumption || 0;
    } else {
      const previous = history[index - 1];
      if (number(row.total) != null && number(previous.total) != null && previous.time >= cutoff) {
        consumed += Math.max(0, Number(previous.total) - Number(row.total));
        legacy = true;
      }
    }
  });
  const sufficient = days >= 1 && observations >= 3;
  const rate = sufficient ? consumed / days : null;
  const status = available == null ? "unknownStock" : available <= 0 ? "outOfStock" : !sufficient ? "insufficientHistory" : rate <= 0 ? "noDepletion" : "forecastReady";
  const zeroTime = available != null && available <= 0 ? now : rate > 0 && available != null ? now + available / rate * DAY : null;
  const thresholdTime = available == null ? null : available <= alertQuantity ? now : rate > 0 ? now + (available - alertQuantity) / rate * DAY : null;
  return { history, available, physical, start, days, observations, rate, status, zeroTime, thresholdTime, legacy, lastUpdated: latest?.time, ...incomingDeliveries(rows, now, warehouseName) };
};

export const forecastChart = (forecast, { now, historyDays, horizonDays }) => {
  const cutoff = historyCutoff(forecast.history, now, historyDays);
  const data = forecast.history.filter((row) => row.time >= cutoff && row.time < now).map((row) => ({
    time: row.time, available: number(row.free), physical: number(row.total), ...row.events,
  }));
  const previous = forecast.history.filter((row) => row.time < cutoff).pop();
  if (previous) data.unshift({ time: cutoff, available: number(previous.free), physical: number(previous.total) });
  const hasRate = forecast.rate != null;
  const rate = forecast.rate || 0;
  if (forecast.available == null) return data;
  data.push({ time: now, available: forecast.available, physical: forecast.physical,
    baseline: hasRate ? forecast.available : null, withDeliveries: hasRate ? forecast.available : null,
    ...forecast.history.filter((row) => row.time === now).reduce((events, row) => {
      Object.entries(row.events).forEach(([key, value]) => { events[key] = (events[key] || 0) + value; });
      return events;
    }, {}),
  });
  if (!hasRate) return data;
  const end = now + horizonDays * DAY;
  const times = new Set([end]);
  for (let day = 1; day <= horizonDays; day++) times.add(now + day * DAY);
  [forecast.zeroTime, forecast.thresholdTime].forEach((time) => { if (time > now && time < end) times.add(time); });
  forecast.deliveries.forEach((delivery) => { if (delivery.time <= end) times.add(delivery.time); });
  let incoming = 0;
  [...times].sort((a, b) => a - b).forEach((time) => {
    const baseline = forecast.available - rate * (time - now) / DAY;
    const deliveries = forecast.deliveries.filter((delivery) => delivery.time === time);
    if (deliveries.length) {
      data.push({ time, baseline, withDeliveries: baseline + incoming });
      incoming += deliveries.reduce((sum, delivery) => sum + delivery.quantity, 0);
    }
    data.push({ time, baseline, withDeliveries: baseline + incoming,
      delivery: deliveries.length ? baseline + incoming : null,
      incomingQuantity: deliveries.reduce((sum, delivery) => sum + delivery.quantity, 0) || null });
  });
  return data;
};
